import { useEffect, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { copyText } from "@/lib/clipboard";
import { toast } from "sonner";
import {
  Loader2, Check, Upload, Plus, Trash2, ShieldCheck, ArrowRight, ArrowLeft,
  CheckCircle2, Building2, Users, FileText, ExternalLink, Globe, Sparkles, AlertTriangle, MapPin, ChevronDown,
} from "lucide-react";

import VendorProposals from "@/pages/fs/VendorProposals";
import VendorWorkOrders from "@/pages/fs/VendorWorkOrders";
import VendorAlerts from "@/pages/fs/VendorAlerts";

const CAT_LABEL = {
  common_area_cleaning: "Common-area cleaning", trash: "Trash & garbage", landscaping: "Landscaping",
  plumbing: "Plumbing", electrical: "Electrical", hvac: "HVAC", pest_control: "Pest control",
  pool: "Pool", handyman: "Handyman", janitorial: "Janitorial", other: "Other",
};

const TAX_CLASSES = [
  "", "Sole proprietor", "Single-member LLC", "LLC (partnership)", "LLC (S-corp)",
  "LLC (C-corp)", "S-Corporation", "C-Corporation", "Partnership", "Non-profit",
];

const AGREEMENT = [
  ["Working together", "There's no cost to you for this service, and either side can stop at any time by telling the other in writing. Nothing here changes the service agreement you already have with the management company — it only changes how requests reach you."],
  ["How we keep in touch", "We communicate with you and your crew by text and email so you get real-time updates on jobs, requests and proposals. For recurring or scheduled work, you can name the person on site to receive jobs directly, with your office copied at the same time — and your office is always the fallback. You can change who is contacted for any property at any time."],
  ["You remain in charge of your people", "Your company stays the contracted party. Your office keeps full supervisory control, scheduling and direction of your employees. Notifying a named crew member is a convenience for speed — it does not make the management company or us their employer, and it does not create any employment relationship."],
  ["Texts and emails", "You and each person you add agree to receive work-related texts and emails at the numbers and addresses you give us. Each person confirms this for themselves and can reply STOP at any time; their jobs then go to your office instead. Message and data rates may apply."],
  ["Insurance", "You will keep a current certificate of insurance on file, listing the coverages the community requires. We will remind you before it expires. If it lapses, we will not send new work to your crew until a current certificate is uploaded."],
  ["Photos and records", "Photos, timestamps, checklists and messages relating to a job are records of the work and are visible to the management company and its board. Do not send anything unrelated to the work."],
  ["Privacy", "Crew phone numbers are never shown to residents. Messages to and from residents go through the community's number."],
];

const ENGAGEMENT = [
  ["recurring", "Recurring / scheduled work", "Weekly cleaning, landscaping, pool, trash — regular visits."],
  ["on_demand", "On-demand & project bids", "One-off jobs, repairs, and bidding on RFPs."],
  ["other", "Something else", "Tell us how you work below."],
];

export default function VendorOnboarding() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [step, setStep] = useState(1);
  const [f, setF] = useState({});
  const [crew, setCrew] = useState({ name: "", phone: "", email: "", roles: ["tech"] });
  const [agreed, setAgreed] = useState(false);
  const [signedBy, setSignedBy] = useState("");
  const [busy, setBusy] = useState(false);
  const [advOpen, setAdvOpen] = useState({});
  const [availableRoles, setAvailableRoles] = useState([
    { key: "tech", label: "Field crew — does the work" },
    { key: "dispatcher", label: "Office / dispatcher" },
    { key: "billing", label: "Billing only" },
  ]);
  const [extracting, setExtracting] = useState(false);
  const [marketInput, setMarketInput] = useState("");
  const [coiExtracted, setCoiExtracted] = useState(null);
  const fileRef = useRef(null);
  const w9Ref = useRef(null);
  const bootstrapped = useRef(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/fs/vendor/${token}`);
      setD(data);
      setAvailableRoles(data.available_roles || availableRoles);
      const coi = data.vendor.coi || {};
      if (coi.extracted && Object.keys(coi.extracted).length) setCoiExtracted(coi.extracted);
      const addr = data.vendor.address_struct || {};
      setF({
        name: data.vendor.name || "", dba: data.vendor.dba || "", ein: data.vendor.ein || "",
        address: data.vendor.address || "", phone: data.vendor.phone || "", email: data.vendor.email || "",
        website: data.vendor.website || "",
        addr_street: addr.street || "", addr_unit: addr.unit || "", addr_city: addr.city || "",
        addr_state: addr.state || "", addr_zip: addr.zip || "",
        tax_classification: (data.vendor.tax || {}).classification || "",
        website_profile: (data.vendor.website_profile || {}).summary || "",
        trades: data.vendor.trades || [], markets: data.vendor.markets || [],
        licenses: data.vendor.licenses || [], default_language: data.vendor.default_language || "en",
        engagement_types: data.vendor.engagement_types || [], engagement_other: data.vendor.engagement_other || "",
        has_dispatcher: data.vendor.has_dispatcher,
        gl_limit: coi.gl_limit || "", wc: coi.wc ?? true, addl_insured: coi.addl_insured ?? true,
        expires_on: coi.expires_on || "", carrier: coi.carrier || "",
      });
      if (!bootstrapped.current) {
        bootstrapped.current = true;
        if (["pending_approval", "active", "rejected"].includes(data.vendor.status)) setStep(6);
        else if (data.step > 1 && data.step <= 5) setStep(data.step);
      }
    } catch { setD({ error: true }); }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  if (!d) return <div className="grid min-h-screen place-items-center bg-sand-50"><Loader2 className="h-6 w-6 animate-spin text-ink/30" /></div>;
  if (d.error) return (
    <div className="grid min-h-screen place-items-center bg-sand-50 px-6 text-center">
      <p className="text-base text-ink/60">That link isn't valid any more.</p>
    </div>
  );

  const saveStep = async (n, extra) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/fs/vendor/${token}/step`, { step: n, ...f, ...(extra || {}) });
      setD(data); setStep(n + 1);
    } catch (e) { toast.error(errMsg(e, "Try again")); }
    setBusy(false);
  };
  const extractSite = async () => {
    if (!(f.website || "").trim()) return toast.error("Add your website first");
    setExtracting(true);
    try {
      const { data } = await api.post(`/fs/vendor/${token}/extract-website`, { website: f.website });
      if (data.summary) { setF((p) => ({ ...p, website_profile: data.summary })); toast.success("We read your site — check it over below"); }
      else toast.message("We couldn't pull much — add a short description yourself");
    } catch (e) { toast.error(errMsg(e, "Couldn't read that site")); }
    setExtracting(false);
  };
  const commitMarkets = (raw) => {
    const parts = String(raw).split(",").map((s) => s.trim()).filter(Boolean);
    if (!parts.length) return;
    setF((p) => {
      const cur = p.markets || [];
      const seen = new Set(cur.map((x) => x.toLowerCase()));
      const add = [];
      for (const m of parts) {
        const lc = m.toLowerCase();
        if (!seen.has(lc)) { seen.add(lc); add.push(m.slice(0, 80)); }
      }
      return { ...p, markets: [...cur, ...add] };
    });
  };
  const addMarket = () => { commitMarkets(marketInput); setMarketInput(""); };
  const toggleEngagement = (k) => setF((p) => {
    const cur = p.engagement_types || [];
    return { ...p, engagement_types: cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k] };
  });
  const setLicense = (i, patch) => setF((p) => { const ls = [...(p.licenses || [])]; ls[i] = { ...ls[i], ...patch }; return { ...p, licenses: ls }; });
  const addLicense = () => setF((p) => ({ ...p, licenses: [...(p.licenses || []), { type: "", number: "", state: "", expires_on: "" }] }));
  const removeLicense = (i) => setF((p) => ({ ...p, licenses: (p.licenses || []).filter((_, j) => j !== i) }));
  const addCrew = async () => {
    if (!crew.name.trim()) return toast.error("Name is required");
    setBusy(true);
    try {
      await api.post(`/fs/vendor/${token}/contacts`, crew);
      toast.success(d.self_apply
        ? `${crew.name} added — they'll get their setup text once a community adds you`
        : `${crew.name} has been texted their own quick setup`);
      setCrew({ name: "", phone: "", email: "", roles: ["tech"] });
      await load();
    } catch (e) { toast.error(errMsg(e, "Could not add them")); }
    setBusy(false);
  };
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post(`/fs/vendor/${token}/coi`, fd);
      const ex = data.extracted || {};
      setCoiExtracted(Object.keys(ex).length ? ex : null);
      setF((p) => ({
        ...p,
        carrier: p.carrier || ex.carrier || "",
        gl_limit: p.gl_limit || ex.gl_limit || "",
        expires_on: p.expires_on || ex.expires_on || "",
        wc: typeof ex.wc === "boolean" ? ex.wc : p.wc,
        addl_insured: typeof ex.addl_insured === "boolean" ? ex.addl_insured : p.addl_insured,
      }));
      setD((prev) => (prev ? { ...prev, vendor: { ...prev.vendor, coi: data.coi } } : prev));
      toast.success(Object.keys(ex).length ? "Certificate read — we filled in what we found" : "Certificate uploaded");
    } catch (er) { toast.error(errMsg(er, "Upload failed")); }
    setBusy(false);
  };
  const uploadW9 = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post(`/fs/vendor/${token}/w9`, fd); setD((prev) => (prev ? { ...prev, vendor: { ...prev.vendor, tax: data.tax } } : prev)); toast.success("W-9 uploaded"); }
    catch (er) { toast.error(errMsg(er, "Upload failed")); }
    setBusy(false);
  };
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/fs/vendor/${token}/submit`, { agreed, signed_by: signedBy, version: "v1" });
      setStep(6); await load();
    } catch (e) { toast.error(errMsg(e, "Try again")); }
    setBusy(false);
  };

  const T = (k, ph, testid, type = "text") => (
    <label className="block">
      <span className="mb-1 block text-xs font-bold uppercase tracking-wider text-ink/40">{ph}</span>
      <input data-testid={testid} type={type} value={f[k] ?? ""} onChange={(e) => setF({ ...f, [k]: e.target.value })}
        className="w-full rounded-xl border border-sand-300 bg-white px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />
    </label>
  );
  const Nav = ({ back, next, nextLabel = "Continue", disabled, testid }) => (
    <div className="mt-7 flex gap-2">
      {back && (
        <button data-testid="vo-back" onClick={() => setStep(back)}
          className="inline-flex items-center gap-1.5 rounded-xl border border-sand-300 bg-white px-4 py-3 text-sm font-semibold text-ink/60">
          <ArrowLeft className="h-4 w-4" />
        </button>
      )}
      <button data-testid={testid} onClick={next} disabled={busy || disabled}
        className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-redline-500 px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-redline-600 disabled:opacity-50">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}{nextLabel}<ArrowRight className="h-4 w-4" />
      </button>
    </div>
  );

  const techs = (d.contacts || []).filter((c) => (c.roles || []).includes("tech"));
  const expiryMismatch = coiExtracted && coiExtracted.expires_on && f.expires_on && coiExtracted.expires_on !== f.expires_on;
  const selfApply = d.self_apply;
  // A vendor with no community attached is joining the NETWORK — whether they applied
  // themselves or Redline invited them. Without this they were greeted by a literal "null".
  const networkOnly = d.self_apply || !(d.communities || []).length;
  const appName = d.app_name || "Redline";
  const needsCrewSetup = (d.vendor.engagement_types || []).includes("recurring");

  return (
    <div className="min-h-screen bg-sand-50 pb-16" data-testid="vendor-onboarding">
      <div className="mx-auto max-w-xl px-5 pt-10">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-redline-600" data-testid="vo-scope-line">
              {(d.communities || []).length > 1
                ? `${d.communities.length} properties`
                : (networkOnly ? `${appName} vendor network` : (d.community.name || "Vendor setup"))}
            </div>
            <div className="text-xs font-semibold text-ink/40">{d.vendor.name}</div>
          </div>
          {step < 6 && <div className="text-xs font-bold text-ink/35">{step} / 5</div>}
        </div>
        <div className="mt-3 h-1 overflow-hidden rounded-full bg-sand-200">
          <div className="h-full rounded-full bg-redline-500 transition-all duration-500" style={{ width: `${(Math.min(step, 5) / 5) * 100}%` }} />
        </div>

        {step === 1 && (
          <div className="mt-7">
            <h1 className="font-heading text-2xl font-extrabold leading-tight text-ink">
              {networkOnly
                ? `Join the ${appName} vendor network.`
                : `${d.community.name} would like to add you as a vendor.`}
            </h1>
            <div className="mt-4 space-y-2.5">
              {(networkOnly
                ? [["Get invited to the right jobs.",
                    "When a community needs your trade in your area, you get a text with a link to see the job and bid — no account, no password."],
                   ["Send proposals and win recurring work.",
                    "Put in a price on RFPs and one-off jobs, or get named for regular scheduled visits."],
                   ["Real-time updates by text and email.",
                    "Once a community brings you on, jobs and messages reach you and your crew instantly. You always stay in charge of your people."]]
                : [["A few quick details to get you set up.",
                    "Your business, the trades you cover, and where you work — about five minutes."],
                   ["Bid, get scheduled, and get paid.",
                    "You'll be able to receive jobs, send proposals, and take on recurring visits."],
                   ["Real-time updates by text and email.",
                    "Jobs and messages reach you and your crew instantly. No account, no password — everything runs from a text link."]]
              ).map(([h, s], i) => (
                <div key={i} className="rounded-2xl bg-white px-4 py-4">
                  <div className="text-sm font-bold text-ink">{h}</div>
                  <div className="mt-1 text-sm leading-relaxed text-ink/55">{s}</div>
                </div>
              ))}
            </div>
            <Nav next={() => saveStep(1)} nextLabel="Let's go" testid="vo-start" />
          </div>
        )}

        {step === 2 && (
          <div className="mt-7">
            <div className="mb-1 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-ink/40">
              <Building2 className="h-3.5 w-3.5" /> Your business
            </div>
            <h1 className="font-heading text-xl font-extrabold text-ink">Who are we contracting with?</h1>
            <div className="mt-4 space-y-3">
              {T("name", "Legal business name", "vo-name")}
              {T("dba", "Doing business as (optional)", "vo-dba")}
              <div className="grid gap-3 sm:grid-cols-2">
                {T("ein", "EIN / Tax ID", "vo-ein")}
                {T("phone", "Office phone", "vo-phone")}
              </div>
              {T("email", "Billing email", "vo-email", "email")}

              {/* Structured address */}
              <div className="rounded-2xl border border-sand-200 bg-white p-4">
                <div className="mb-2 text-xs font-bold uppercase tracking-wider text-ink/40">Business address <span className="text-redline-600">(required)</span></div>
                <div className="space-y-2">
                  {T("addr_street", "Street address", "vo-addr-street")}
                  <div className="grid gap-2 sm:grid-cols-2">
                    {T("addr_unit", "Suite / unit (optional)", "vo-addr-unit")}
                    {T("addr_city", "City", "vo-addr-city")}
                  </div>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {T("addr_state", "State", "vo-addr-state")}
                    {T("addr_zip", "ZIP", "vo-addr-zip")}
                  </div>
                  <p className="mt-1 text-xs text-ink/45">We need a physical address on file for insurance and payments.</p>
                </div>
              </div>

              {/* Tax classification */}
              <label className="block">
                <span className="mb-1 block text-xs font-bold uppercase tracking-wider text-ink/40">Tax classification (for your W-9)</span>
                <select data-testid="vo-tax-class" value={f.tax_classification || ""}
                  onChange={(e) => setF({ ...f, tax_classification: e.target.value })}
                  className="w-full rounded-xl border border-sand-300 bg-white px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-redline-500/30">
                  {TAX_CLASSES.map((t) => <option key={t} value={t}>{t || "Select…"}</option>)}
                </select>
              </label>

              {/* Website + AI extraction */}
              <div className="rounded-2xl border border-sand-200 bg-white p-4">
                <div className="mb-2 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-ink/40">
                  <Globe className="h-3.5 w-3.5" /> Website (optional)
                </div>
                <div className="flex gap-2">
                  <input data-testid="vo-website" value={f.website || ""} placeholder="yourcompany.com"
                    onChange={(e) => setF({ ...f, website: e.target.value })}
                    className="min-w-0 flex-1 rounded-xl border border-sand-300 bg-white px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />
                  <button data-testid="vo-website-extract" onClick={extractSite} disabled={extracting || !(f.website || "").trim()}
                    className="inline-flex shrink-0 items-center gap-1.5 rounded-xl bg-ink px-3.5 py-2.5 text-sm font-bold text-white disabled:opacity-50">
                    {extracting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Read my site
                  </button>
                </div>
                <div className="mt-3">
                  <div className="mb-1 text-xs font-bold uppercase tracking-wider text-ink/40">About your business — edit anything that's off</div>
                  <textarea data-testid="vo-website-profile" value={f.website_profile || ""} rows={4}
                    placeholder="A short description of what you do — tap “Read my site” to fill this from your website, or just type it yourself."
                    onChange={(e) => setF({ ...f, website_profile: e.target.value })}
                    className="w-full rounded-xl border border-sand-300 bg-white px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />
                </div>
              </div>
            </div>
            <Nav back={1} next={() => saveStep(2)} testid="vo-step2"
              disabled={!f.name || !f.addr_street || !f.addr_city || !f.addr_state || !f.addr_zip} />
          </div>
        )}

        {step === 3 && (
          <div className="mt-7">
            <h1 className="font-heading text-xl font-extrabold text-ink">What do you do, and where?</h1>
            <div className="mt-4">
              <div className="mb-2 text-xs font-bold uppercase tracking-wider text-ink/40">Services you accept</div>
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(CAT_LABEL).map(([k, label]) => {
                  const on = (f.trades || []).includes(k);
                  return (
                    <button key={k} data-testid={`vo-trade-${k}`} onClick={() => setF({
                      ...f, trades: on ? f.trades.filter((x) => x !== k) : [...(f.trades || []), k],
                    })} className={`rounded-full px-3.5 py-2 text-sm font-semibold transition-colors ${on ? "bg-redline-500 text-white" : "bg-white text-ink/60"}`}>
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Service area / markets */}
            <div className="mt-6">
              <div className="mb-2 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-ink/40">
                <MapPin className="h-3.5 w-3.5" /> Cities / areas you serve
              </div>
              <div className="flex gap-2">
                <input data-testid="vo-market-input" value={marketInput} placeholder="Type a city, e.g. Austin"
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val.includes(",")) {
                      const parts = val.split(",");
                      const remainder = parts.pop();
                      commitMarkets(parts.join(","));
                      setMarketInput(remainder.replace(/^\s+/, ""));
                    } else setMarketInput(val);
                  }}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addMarket(); } }}
                  className="min-w-0 flex-1 rounded-xl border border-sand-300 bg-white px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />
                <button data-testid="vo-market-add" onClick={addMarket} disabled={!marketInput.trim()}
                  className="inline-flex shrink-0 items-center gap-1.5 rounded-xl bg-ink px-4 py-2.5 text-sm font-bold text-white disabled:opacity-40">
                  <Plus className="h-4 w-4" /> Add
                </button>
              </div>
              <p className="mt-1.5 text-xs text-ink/45">Press <b>Enter</b> or tap <b>Add</b> after each city. You can paste several separated by commas.</p>
              {(f.markets || []).length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {(f.markets || []).map((m, i) => (
                    <span key={i} data-testid={`vo-market-${i}`} className="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-sm font-semibold text-ink/70">
                      {m}
                      <button onClick={() => setF((p) => ({ ...p, markets: p.markets.filter((_, j) => j !== i) }))} className="text-ink/30 hover:text-redline-600">
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Licenses */}
            <div className="mt-6">
              <div className="mb-2 text-xs font-bold uppercase tracking-wider text-ink/40">Trade licenses (optional)</div>
              {(f.licenses || []).map((lic, i) => (
                <div key={i} data-testid={`vo-license-${i}`} className="mb-2 rounded-xl bg-white p-3">
                  <div className="grid gap-2 sm:grid-cols-2">
                    <input data-testid={`vo-license-type-${i}`} value={lic.type || ""} placeholder="Type (e.g. Master plumber)"
                      onChange={(e) => setLicense(i, { type: e.target.value })}
                      className="rounded-lg border border-sand-300 px-3 py-2 text-sm outline-none" />
                    <input data-testid={`vo-license-number-${i}`} value={lic.number || ""} placeholder="License number"
                      onChange={(e) => setLicense(i, { number: e.target.value })}
                      className="rounded-lg border border-sand-300 px-3 py-2 text-sm outline-none" />
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <input data-testid={`vo-license-state-${i}`} value={lic.state || ""} placeholder="State"
                      onChange={(e) => setLicense(i, { state: e.target.value })}
                      className="w-20 rounded-lg border border-sand-300 px-3 py-2 text-sm outline-none" />
                    <input type="date" data-testid={`vo-license-expiry-${i}`} value={lic.expires_on || ""}
                      onChange={(e) => setLicense(i, { expires_on: e.target.value })}
                      className="min-w-0 flex-1 rounded-lg border border-sand-300 px-3 py-2 text-sm outline-none" />
                    <button onClick={() => removeLicense(i)} className="shrink-0 rounded-lg p-2 text-ink/30 hover:text-redline-600">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              ))}
              <button data-testid="vo-license-add" onClick={addLicense}
                className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-sand-300 px-3 py-1.5 text-xs font-bold text-ink/55">
                <Plus className="h-3 w-3" /> Add a license
              </button>
            </div>

            {/* How they work — drives whether crew setup is required after approval */}
            <div className="mt-6">
              <div className="mb-2 text-xs font-bold uppercase tracking-wider text-ink/40">How do you usually work? Pick any that fit.</div>
              <div className="space-y-2">
                {ENGAGEMENT.map(([k, label, sub]) => {
                  const on = (f.engagement_types || []).includes(k);
                  return (
                    <button key={k} type="button" data-testid={`vo-engagement-${k}`} onClick={() => toggleEngagement(k)}
                      className={`flex w-full items-start gap-3 rounded-xl px-4 py-3 text-left transition-colors ${on ? "bg-ink text-white" : "bg-white text-ink/70"}`}>
                      <span aria-hidden className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2 ${on ? "border-white bg-white/20 text-white" : "border-sand-400 bg-white"}`}>
                        {on && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm font-bold">{label}</span>
                        <span className={`block text-xs ${on ? "text-white/70" : "text-ink/45"}`}>{sub}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
              {(f.engagement_types || []).includes("other") && (
                <input data-testid="vo-engagement-other" value={f.engagement_other || ""} placeholder="Tell us how you work"
                  onChange={(e) => setF({ ...f, engagement_other: e.target.value })}
                  className="mt-2 w-full rounded-xl border border-sand-300 bg-white px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />
              )}
            </div>
            <div className="mt-6">
              <div className="mb-2 text-xs font-bold uppercase tracking-wider text-ink/40">Default language for your team</div>
              <div className="grid grid-cols-2 gap-2">
                {[["en", "English"], ["es", "Español"]].map(([k, label]) => (
                  <button key={k} data-testid={`vo-lang-${k}`} onClick={() => setF({ ...f, default_language: k })}
                    className={`rounded-xl px-4 py-3.5 text-sm font-bold transition-colors ${f.default_language === k ? "bg-ink text-white" : "bg-white text-ink/60"}`}>
                    {label}
                  </button>
                ))}
              </div>
              <p className="mt-2 text-xs text-ink/45">Each person can pick their own when they set up their phone.</p>
            </div>
            <Nav back={2} next={() => saveStep(3)} testid="vo-step3"
              disabled={!(f.trades || []).length || !(f.engagement_types || []).length} />
          </div>
        )}

        {step === 4 && (
          <div className="mt-7">
            <div className="mb-1 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-ink/40">
              <ShieldCheck className="h-3.5 w-3.5" /> Insurance
            </div>
            <h1 className="font-heading text-xl font-extrabold text-ink">Your certificate of insurance</h1>
            <p className="mt-2 text-sm leading-relaxed text-ink/55">
              Have it handy? Upload it and we'll read the key details for you to confirm. Don't have it
              on your phone right now? No problem — <b>skip this and come back to this same link later</b>.
              We'll remind you. We just can't send your crew work until a current certificate is on file
              (that protects you as much as the community).
            </p>
            <div className="mt-4 space-y-3">
              <button data-testid="vo-coi-upload" onClick={() => fileRef.current?.click()} disabled={busy}
                className="flex w-full items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-sand-300 bg-white px-5 py-6 text-sm font-bold text-ink/70">
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}
                {(d.vendor.coi || {}).file_path ? "Certificate uploaded — replace it" : "Upload your certificate (PDF or photo)"}
              </button>
              <input ref={fileRef} type="file" accept="application/pdf,image/*" onChange={upload} className="hidden" data-testid="vo-coi-input" />
              {(d.vendor.coi || {}).file_name && <p className="-mt-1 text-xs text-ink/45" data-testid="vo-coi-filename">On file: {d.vendor.coi.file_name}</p>}

              {coiExtracted && Object.keys(coiExtracted).length > 0 && (
                <div data-testid="vo-coi-extracted" className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900">
                  <div className="mb-1 inline-flex items-center gap-1.5 font-bold uppercase tracking-wider">
                    <Sparkles className="h-3.5 w-3.5" /> What we read off the certificate
                  </div>
                  <div className="leading-relaxed">
                    {coiExtracted.carrier && <div>Carrier: {coiExtracted.carrier}</div>}
                    {coiExtracted.policy_no && <div>Policy #: {coiExtracted.policy_no}</div>}
                    {coiExtracted.gl_limit && <div>GL limit: {coiExtracted.gl_limit}</div>}
                    {coiExtracted.expires_on && <div>Expires: {coiExtracted.expires_on}</div>}
                  </div>
                  <div className="mt-1 text-emerald-700">We filled the blanks below — double-check and fix anything that's off.</div>
                </div>
              )}

              <div className="grid gap-3 sm:grid-cols-2">
                {T("carrier", "Insurance carrier", "vo-carrier")}
                {T("gl_limit", "General liability limit", "vo-gl")}
              </div>
              {T("expires_on", "Expiry date", "vo-expiry", "date")}
              {expiryMismatch && (
                <div data-testid="vo-coi-mismatch" className="flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 p-3 text-xs font-semibold text-amber-900">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  Heads up — the date you entered ({f.expires_on}) doesn't match what we read on the certificate ({coiExtracted.expires_on}). Use whichever is correct; your manager will see both.
                </div>
              )}
              <div className="grid gap-2 sm:grid-cols-2">
                {[["wc", "Workers' comp in force"], ["addl_insured", "Community listed as additional insured"]].map(([k, label]) => (
                  <button key={k} data-testid={`vo-${k}`} onClick={() => setF({ ...f, [k]: !f[k] })}
                    className={`flex items-center gap-2.5 rounded-xl px-4 py-3 text-left text-sm font-semibold transition-colors ${f[k] ? "bg-emerald-600 text-white" : "bg-white text-ink/55"}`}>
                    <Check className="h-4 w-4 shrink-0" />{label}
                  </button>
                ))}
              </div>

              {/* Optional W-9 */}
              <div className="rounded-2xl border border-sand-200 bg-white p-4">
                <div className="mb-2 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-ink/40">
                  <FileText className="h-3.5 w-3.5" /> W-9 (optional)
                </div>
                <button data-testid="vo-w9-upload" onClick={() => w9Ref.current?.click()} disabled={busy}
                  className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-sand-300 px-4 py-3 text-sm font-semibold text-ink/60">
                  <Upload className="h-4 w-4" />
                  {(d.vendor.tax || {}).w9_file_path ? "W-9 uploaded — replace it" : "Upload your W-9 (PDF or photo)"}
                </button>
                <input ref={w9Ref} type="file" accept="application/pdf,image/*" onChange={uploadW9} className="hidden" data-testid="vo-w9-input" />
                {(d.vendor.tax || {}).w9_file_name && <p className="mt-1 text-xs text-ink/45" data-testid="vo-w9-filename">On file: {d.vendor.tax.w9_file_name}</p>}
              </div>
            </div>
            <Nav back={3} next={() => saveStep(4)} testid="vo-step5"
              nextLabel={f.expires_on ? "Continue" : "Skip for now — I'll add it later"} />
          </div>
        )}

        {step === 5 && (
          <div className="mt-7">
            <div className="mb-1 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-ink/40">
              <FileText className="h-3.5 w-3.5" /> Agreement
            </div>
            <h1 className="font-heading text-xl font-extrabold text-ink">Vendor agreement</h1>
            <p className="mt-2 text-sm text-ink/55">Short, plain, and it doesn't replace your existing contract.</p>
            <div className="mt-4 max-h-72 space-y-3 overflow-y-auto rounded-2xl bg-white p-5" data-testid="vo-agreement-text">
              {AGREEMENT.map(([h, body]) => (
                <div key={h}>
                  <div className="text-sm font-bold text-ink">{h}</div>
                  <div className="mt-0.5 text-sm leading-relaxed text-ink/60">{body}</div>
                </div>
              ))}
            </div>
            <button data-testid="vo-agree" onClick={() => setAgreed(!agreed)}
              className="mt-3 flex w-full items-start gap-3 rounded-xl border border-sand-300 bg-white px-4 py-3.5 text-left text-sm font-semibold text-ink/70 transition-colors hover:border-ink/40">
              <span aria-hidden className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2 transition-colors ${agreed ? "border-emerald-600 bg-emerald-600 text-white" : "border-sand-400 bg-white"}`}>
                {agreed && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
              </span>
              I've read it and I'm authorized to accept it for {f.name || d.vendor.name}.
            </button>
            <input data-testid="vo-signed-by" value={signedBy} placeholder="Type your full name to sign"
              onChange={(e) => setSignedBy(e.target.value)}
              className="mt-2 w-full rounded-xl border border-sand-300 bg-white px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />
            <Nav back={4} next={submit} nextLabel="Accept & send for approval" testid="vo-submit"
              disabled={!agreed || signedBy.trim().length < 3} />
          </div>
        )}

        {step === 6 && (
          <div className="mt-8" data-testid="vo-done">
            <div className="rounded-3xl bg-white px-6 py-10 text-center">
              <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-600" />
              <h1 className="mt-3 font-heading text-2xl font-extrabold text-ink">
                {d.vendor.status === "active"
                  ? (networkOnly ? "You're in the network" : "You're live")
                  : d.vendor.status === "rejected" ? "We couldn't approve this yet" : "Sent for approval"}
              </h1>
              <p className="mt-2 text-sm text-ink/55">
                {d.vendor.status === "active"
                  ? (networkOnly
                    ? `No community has switched you on yet. You'll get a text the moment one does — and you can bid on any request below.`
                    : `Jobs will come straight to the crew you've named at each property.`)
                  : d.vendor.status === "rejected"
                    ? `Your setup is saved — see the note below.`
                    : networkOnly
                      ? `The ${appName} team will review your application. A community will add you to their property once you're approved.`
                      : ((d.vendor.coi || {}).expires_on)
                        ? `${d.community.name} will review and approve. Nothing else needed from you.`
                        : `${d.community.name} will review and approve. You can already be told about work and come out to look at a job — see below for what we'll need before you can put a price in.`}
              </p>
            </div>

            {/* Registering, hearing about work and walking a property need NOTHING on file —
                that's the owner's decision and it's what keeps the funnel alive. What paperwork
                buys is the right to BID and to TAKE A JOB, so say exactly that. */}
            {!((d.vendor.coi || {}).expires_on) && (
              <div data-testid="vo-docs-outstanding" className="mt-4 rounded-2xl border border-amber-300 bg-amber-50 p-4">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-bold text-amber-900">Before you can bid: your insurance certificate</div>
                    <p className="mt-1 text-xs leading-relaxed text-amber-900/80">
                      You can look at work and come out to see a job right now. Before you can put a
                      price in — or be given a job — we'll need a current certificate of insurance, a
                      W-9 and a signed agreement. Come back to <b>this same link</b> anytime to upload
                      them; we've also texted and emailed it to you so it's easy to find.
                    </p>
                    <div className="mt-3 flex items-center gap-2">
                      <input readOnly data-testid="vo-resume-link" value={`${window.location.origin}/vendor/${token}`}
                        className="min-w-0 flex-1 rounded-lg border border-amber-300 bg-white px-3 py-2 text-xs text-ink/60" />
                      <button data-testid="vo-copy-resume"
                        onClick={() => copyText(`${window.location.origin}/vendor/${token}`, "Link copied — save it so you can come back")}
                        className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-ink px-3 py-2 text-xs font-bold text-white">Copy</button>
                    </div>
                    <button data-testid="vo-upload-now" onClick={() => setStep(4)}
                      className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-amber-900 underline">
                      Or upload it now
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Where they stand with the network, and what it means — a vendor should never have
                to guess, and "rejected" used to look identical to "in review". */}
            <div className="mt-4" data-testid="vo-network-status">
              {d.vendor.status === "rejected" ? (
                <div className="rounded-2xl border border-red-200 bg-red-50 p-5">
                  <div className="font-heading text-base font-bold text-red-800">Not approved right now</div>
                  {d.vendor.network_reject_reason && (
                    <p className="mt-1.5 text-sm text-red-900/80" data-testid="vo-reject-reason">
                      <span className="font-semibold">Why:</span> {d.vendor.network_reject_reason}
                    </p>
                  )}
                  <p className="mt-2 text-sm leading-relaxed text-red-900/70">
                    If that's something you can put right — a current certificate, a signed W-9 — update it
                    above and reply to the email we sent. We'll take another look.
                  </p>
                </div>
              ) : d.vendor.status === "active" ? (
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5">
                  <div className="font-heading text-base font-bold text-emerald-900">
                    You're in the {appName} network
                  </div>
                  <ul className="mt-2 space-y-1.5 text-sm leading-relaxed text-emerald-900/75">
                    <li>· Any community we manage can add you — you never apply twice.</li>
                    <li>· Jobs arrive as a text with a link. No app, no password.</li>
                    <li>· One certificate covers every property. If it lapses, new jobs pause until you send the new one.</li>
                  </ul>
                </div>
              ) : (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5">
                  <div className="font-heading text-base font-bold text-amber-900">In review</div>
                  <p className="mt-1.5 text-sm leading-relaxed text-amber-900/75">
                    We're checking three things: your insurance certificate is current, your W-9 is on file,
                    and the agreement is signed. You'll get a text and an email either way — nothing else is
                    needed from you.
                  </p>
                </div>
              )}
            </div>

            <VendorAlerts token={token} />

            <div className="mt-6" data-testid="vo-crew-setup">
              {needsCrewSetup && !techs.length && (
                <div data-testid="vo-crew-required" className="mb-3 flex items-start gap-2 rounded-2xl border border-amber-300 bg-amber-50 p-4">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-bold text-amber-900">One thing left: add your crew</div>
                    <p className="mt-1 text-xs leading-relaxed text-amber-900/80">
                      You told us you do recurring or scheduled work. Add the person who'll be on site so jobs
                      and updates reach them by text and email the moment they come in — with your office copied.
                      Until then, jobs go to your office.
                    </p>
                  </div>
                </div>
              )}
              <div className="rounded-2xl bg-white p-5">
                <div className="mb-1 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-ink/40">
                  <Users className="h-3.5 w-3.5" /> Your crew
                </div>
                <p className="text-sm leading-relaxed text-ink/55">
                  Add each person once. They each get their own text, verify their own phone and pick their
                  language — that's what lets us send jobs and real-time updates straight to them. You stay in
                  charge of your people.
                </p>
                <div className="mt-3 space-y-2">
                  {(d.contacts || []).map((c) => (
                    <div key={c.id} data-testid={`vo-contact-${c.id}`} className="flex items-center justify-between gap-2 rounded-xl bg-sand-50 px-4 py-3">
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-bold text-ink">{c.name}
                          <span className="ml-2 text-xs font-normal text-ink/40">{(c.roles || []).join(" · ")}</span>
                        </div>
                        <div className="text-xs text-ink/40">{c.phone || c.email}</div>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${
                          c.verify_status === "verified" ? "bg-emerald-50 text-emerald-700"
                            : c.verify_status === "stopped" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-800"}`}>
                          {c.verify_status === "verified" ? "Ready" : c.verify_status === "stopped" ? "Opted out" : "Waiting on them"}
                        </span>
                        <button data-testid={`vo-remove-${c.id}`} onClick={async () => {
                          await api.delete(`/fs/vendor/${token}/contacts/${c.id}`); await load();
                        }} className="rounded-lg p-1.5 text-ink/25 hover:text-redline-600"><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-3 space-y-2 rounded-2xl border border-dashed border-sand-300 bg-sand-50/60 p-4">
                  <div className="grid gap-2 sm:grid-cols-2">
                    <input data-testid="vo-crew-name" value={crew.name} placeholder="Name" onChange={(e) => setCrew({ ...crew, name: e.target.value })}
                      className="rounded-xl border border-sand-300 bg-white px-3.5 py-2.5 text-sm outline-none" />
                    <input data-testid="vo-crew-phone" value={crew.phone} placeholder="Mobile number" onChange={(e) => setCrew({ ...crew, phone: e.target.value })}
                      className="rounded-xl border border-sand-300 bg-white px-3.5 py-2.5 text-sm outline-none" />
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {availableRoles.map((r) => {
                      const on = (crew.roles || []).includes(r.key);
                      return (
                        <button key={r.key} type="button" data-testid={`vo-crew-role-${r.key}`}
                          onClick={() => setCrew({ ...crew, roles: on ? crew.roles.filter((x) => x !== r.key) : [...crew.roles, r.key] })}
                          className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${on ? "bg-ink text-white" : "border border-sand-300 bg-white text-ink/55 hover:border-ink/30"}`}>
                          {on && <Check className="mr-1 inline h-3 w-3" strokeWidth={3} />}{r.label}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-xs text-ink/45">Tick everything this person does — one person can be the office <span className="font-semibold">and</span> the one doing the work.</p>
                  <button data-testid="vo-crew-add" onClick={addCrew} disabled={busy || !(crew.roles || []).length || !crew.name.trim()}
                    className="inline-flex w-full items-center justify-center gap-1.5 rounded-xl bg-ink px-4 py-2.5 text-sm font-bold text-white disabled:opacity-50">
                    <Plus className="h-4 w-4" /> Add & text them
                  </button>
                </div>
              </div>
            </div>

            {/* One block per property — a cleaner on three buildings names a different person at each */}
            <div className="mt-6">
              <div className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-ink/40">
                <Building2 className="h-3.5 w-3.5" /> Your properties ({(d.communities || []).length})
              </div>
              {(d.communities || []).length === 0 && (
                <div className="rounded-2xl bg-white p-5 text-sm text-ink/55" data-testid="vo-no-properties">
                  A community will add you to their property once the {appName} team approves your application.
                </div>
              )}
              {(d.communities || []).map((cm) => {
                const s = cm.site_assignment || {};
                const setSite = async (patch) => {
                  setBusy(true);
                  try {
                    const { data } = await api.put(`/fs/vendor/${token}/site/${cm.id}`, {
                      primary_contact_id: s.primary_contact_id || null,
                      backup_contact_id: s.backup_contact_id || null,
                      coverage: s.coverage || [], ...patch,
                    });
                    setD(data); toast.success("Saved");
                  } catch (e) { toast.error(errMsg(e, "Could not save")); }
                  setBusy(false);
                };
                return (
                  <div key={cm.id} data-testid={`vo-property-${cm.id}`} className="mb-3 rounded-2xl bg-white p-5">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="font-heading text-base font-bold text-ink">{cm.name}</div>
                      <div className="flex items-center gap-2 text-xs">
                        <span className={`rounded-full px-2 py-0.5 font-bold uppercase ${
                          cm.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-800"}`}>
                          {cm.status === "active" ? "Live" : "Awaiting approval"}
                        </span>
                        {cm.open_jobs > 0 && <span className="font-semibold text-ink/45">{cm.open_jobs} open</span>}
                      </div>
                    </div>

                    <div className="mt-3">
                      <label className="block">
                        <span className="mb-1 block text-xs font-bold uppercase tracking-wider text-ink/40">Who's on site here</span>
                        <select data-testid={`vo-site-primary-${cm.id}`} value={s.primary_contact_id || ""}
                          onChange={(e) => setSite({ primary_contact_id: e.target.value || null })}
                          className="w-full rounded-xl border border-sand-300 bg-white px-3 py-2.5 text-sm outline-none">
                          <option value="">— nobody yet (jobs go to the office) —</option>
                          {techs.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.name}{c.verify_status !== "verified" ? " (still setting up their phone)" : ""}
                            </option>
                          ))}
                        </select>
                        <p className="mt-1.5 text-xs text-ink/45">That's all most companies need — jobs here text this person. You can stop here.</p>
                      </label>

                      <button type="button" data-testid={`vo-adv-toggle-${cm.id}`}
                        onClick={() => setAdvOpen((p) => ({ ...p, [cm.id]: !p[cm.id] }))}
                        className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-ink/50 hover:text-ink/75">
                        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${advOpen[cm.id] ? "rotate-180" : ""}`} />
                        Advanced (optional): a backup person and set hours
                      </button>

                      {advOpen[cm.id] && (
                        <div className="mt-3 space-y-4 rounded-xl border border-sand-200 bg-sand-50/60 p-3">
                          <label className="block">
                            <span className="mb-1 block text-xs font-bold uppercase tracking-wider text-ink/40">Backup</span>
                            <select data-testid={`vo-site-backup-${cm.id}`} value={s.backup_contact_id || ""}
                              onChange={(e) => setSite({ backup_contact_id: e.target.value || null })}
                              className="w-full rounded-xl border border-sand-300 bg-white px-3 py-2.5 text-sm outline-none">
                              <option value="">— none —</option>
                              {techs.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                          </label>
                          <div>
                            <div className="mb-1.5 text-xs font-bold uppercase tracking-wider text-ink/40">
                              When they're on site here
                            </div>
                            {(s.coverage || []).map((w, i) => (
                              <div key={i} className="mb-2 rounded-xl bg-white p-2.5">
                                <div className="flex gap-1">
                                  {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((dn, di) => {
                                    const on = (w.dow || []).includes(di);
                                    return (
                                      <button key={dn} data-testid={`vo-cov-${cm.id}-${i}-${di}`} onClick={() => {
                                        const cov = [...(s.coverage || [])];
                                        cov[i] = { ...w, dow: on ? (w.dow || []).filter((x) => x !== di) : [...(w.dow || []), di] };
                                        setSite({ coverage: cov });
                                      }} className={`h-9 flex-1 rounded-lg text-[11px] font-bold ${on ? "bg-ink text-white" : "bg-sand-100 text-ink/45"}`}>{dn}</button>
                                    );
                                  })}
                                </div>
                                <div className="mt-2 flex items-center gap-2">
                                  <input type="time" value={w.start || "06:00"} data-testid={`vo-cov-start-${cm.id}-${i}`}
                                    onChange={(e) => { const cov = [...(s.coverage || [])]; cov[i] = { ...w, start: e.target.value }; setSite({ coverage: cov }); }}
                                    className="min-w-0 flex-1 rounded-lg border border-sand-300 bg-white px-2 py-2 text-sm" />
                                  <span className="shrink-0 text-xs font-semibold text-ink/40">to</span>
                                  <input type="time" value={w.end || "14:00"} data-testid={`vo-cov-end-${cm.id}-${i}`}
                                    onChange={(e) => { const cov = [...(s.coverage || [])]; cov[i] = { ...w, end: e.target.value }; setSite({ coverage: cov }); }}
                                    className="min-w-0 flex-1 rounded-lg border border-sand-300 bg-white px-2 py-2 text-sm" />
                                  <button onClick={() => setSite({ coverage: (s.coverage || []).filter((_, j) => j !== i) })}
                                    className="shrink-0 rounded-lg p-2 text-ink/30 hover:text-redline-600"><Trash2 className="h-3.5 w-3.5" /></button>
                                </div>
                              </div>
                            ))}
                            <button data-testid={`vo-cov-add-${cm.id}`} onClick={() => setSite({
                              coverage: [...(s.coverage || []), { dow: [0, 1, 2, 3, 4, 5], start: "06:00", end: "14:00" }],
                            })} className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-sand-300 px-3 py-1.5 text-xs font-bold text-ink/55">
                              <Plus className="h-3 w-3" /> Add hours
                            </button>
                            <p className="mt-2 text-xs leading-relaxed text-ink/45">
                              Leave this blank and jobs can reach your person anytime. Add hours only if
                              you want jobs to go to the office outside certain times.
                            </p>
                          </div>
                        </div>
                      )}
                    </div>

                    {(cm.schedules || []).length > 0 && (
                      <div className="mt-3 border-t border-sand-200 pt-3">
                        <div className="mb-1 text-xs font-bold uppercase tracking-wider text-ink/40">Recurring here</div>
                        {cm.schedules.map((sc, i) => (
                          <div key={i} className="text-xs text-ink/55">
                            {sc.name} — {(sc.dow || []).map((x) => ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"][x]).join(", ")} · {sc.start}–{sc.end}
                            {sc.photo_required ? " · photos required" : ""}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="mt-5 rounded-2xl border border-redline-200 bg-redline-50/60 px-4 py-3.5" data-testid="vo-home-note">
              <div className="text-sm font-bold text-ink">This page is your home.</div>
              <p className="mt-0.5 text-xs leading-relaxed text-ink/60">
                Bookmark it. Lost the link? Sign back in anytime at <span className="font-semibold text-ink/80">askredline.com/vendor</span> with
                your mobile number or email — we'll text and email you a code. Come here to update your
                info or send proposals whenever you need.
              </p>
            </div>

            <VendorProposals token={token} />

            <VendorWorkOrders token={token} />

            {(d.open_jobs || []).length > 0 && (
              <div className="mt-6">
                <div className="mb-2 text-xs font-bold uppercase tracking-wider text-ink/40">Open jobs</div>
                {d.open_jobs.map((j) => (
                  <a key={j.id} href={j.job_url} data-testid={`vo-job-${j.id}`}
                    className="mb-2 flex items-center justify-between gap-2 rounded-xl bg-white px-4 py-3">
                    <div className="min-w-0 flex-1">
                      <div className="font-mono text-xs font-bold text-ink/40">{j.wo.ref} · {j.community}</div>
                      <div className="truncate text-sm font-semibold text-ink">{j.wo.summary}</div>
                      <div className="text-xs text-ink/40">{j.wo.location}</div>
                    </div>
                    <ExternalLink className="h-4 w-4 shrink-0 text-ink/30" />
                  </a>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
